import * as React from 'react';
import { useSnackbar } from 'notistack';
import { Button, Grid, IconButton, Paper, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { IOpeningHoursOverride } from '@pepperhq/types-sdk';

import { Panel } from 'ui/Panel';
import { Location, LocationDayOpeningHours, LocationOverrideOpeningHours } from '../../models/LocationModel';
import { locationApi } from '../../LocationApi';
import { apiOverrideToFormModel, formModelToApiOverride } from '../../openingHoursConverters';
import { formatOverrideDate, formatRange } from '../../locationOpeningHoursHelpers';
import { LocationDateOverrideModal } from './LocationDateOverrideModal';
import { Box } from '@mui/system';
import { MESSAGE_LOCATIONS_UPDATE_ERROR, MESSAGE_LOCATIONS_UPDATE_SUCCESS } from 'config/messages';

interface IProps {
    currentLocation: Location;
    setCurrentLocation: (location: Location) => void;
}

const DateOpeningHours = ({
    dateStr,
    ranges,
    index
}: {
    dateStr: string;
    ranges: LocationDayOpeningHours[];
    index: number;
}) => {
    const formattedRanges = ranges.map(formatRange).filter(Boolean);
    return (
        <Grid
            container
            sx={{
                py: 0.5,
                px: 1,
                backgroundColor: index % 2 === 0 ? 'primary.contrastText' : 'transparent'
            }}
        >
            <Grid size={4}>
                <Typography key={dateStr} variant="body2">
                    {formatOverrideDate(dateStr)}
                </Typography>
            </Grid>
            <Grid size={8} textAlign="right">
                {formattedRanges.length <= 0 && <Typography variant="body2">Closed</Typography>}
                {formattedRanges.map((formatted, index) => (
                    <Typography variant="body2" key={`date-opening-hours-${dateStr}-${index}`}>
                        {formatted}
                    </Typography>
                ))}
            </Grid>
        </Grid>
    );
};

interface OverrideItemProps {
    apiOverride: IOpeningHoursOverride<string>;
    isEditing: boolean;
    isSaving: boolean;
    onEdit: (formOverride: LocationOverrideOpeningHours) => void;
    onDelete: (id: string) => void;
}

const OverrideItem: React.FC<OverrideItemProps> = ({
    apiOverride,
    isEditing,
    isSaving,
    onEdit,
    onDelete
}) => {
    const formOverride = React.useMemo(() => apiOverrideToFormModel(apiOverride), [apiOverride]);

    const handleEdit = React.useCallback(() => {
        onEdit(formOverride);
    }, [onEdit, formOverride]);

    const handleDelete = React.useCallback(() => {
        onDelete(apiOverride._id);
    }, [onDelete, apiOverride._id]);

    const sortedDates = Object.entries(formOverride.dates ?? {}).sort(([a], [b]) => a.localeCompare(b));

    return (
        <Paper elevation={0} variant="outlined" sx={{ position: 'relative', overflow: 'hidden' }}>
            <Box p={1}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {apiOverride.name}
                </Typography>
            </Box>
            <Box display="flex" flexDirection="column">
                {sortedDates.length === 0 ? (
                    <Typography variant="body1" color="text.secondary" sx={{ px: 1 }}>
                        No dates configured
                    </Typography>
                ) : (
                    sortedDates.map(([dateStr, ranges], index) => (
                        <DateOpeningHours key={dateStr} dateStr={dateStr} ranges={ranges} index={index} />
                    ))
                )}
            </Box>
            {isEditing && (
                <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="flex-end"
                    style={{ position: 'absolute', top: 4, right: 8, width: 'fit-content' }}
                >
                    <IconButton size="small" onClick={handleEdit} disabled={isSaving}>
                        <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={handleDelete} disabled={isSaving}>
                        <DeleteIcon />
                    </IconButton>
                </Box>
            )}
        </Paper>
    );
};

export const LocationDateOverridesCard: React.FC<IProps> = ({ currentLocation, setCurrentLocation }) => {
    const [isEditing, setIsEditing] = React.useState(false);
    const [showAddDateOverride, setShowAddDateOverride] = React.useState(false);
    const [dateOverride, setDateOverride] = React.useState<LocationOverrideOpeningHours | null>(null);
    const [overridesState, setOverridesState] = React.useState<IOpeningHoursOverride<string>[]>(
        () => currentLocation.openingHours?.overrides ?? []
    );
    const [isSaving, setIsSaving] = React.useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const onSubmit = React.useCallback((formOverride: LocationOverrideOpeningHours) => {
        const apiOverride = formModelToApiOverride(formOverride);
        setOverridesState(existing =>
            apiOverride._id
                ? existing.map(o => (o._id === apiOverride._id ? apiOverride : o))
                : [...existing, apiOverride]
        );
        setShowAddDateOverride(false);
        setDateOverride(null);
    }, []);

    const handleSave = React.useCallback(async () => {
        setIsSaving(true);
        const response = await locationApi.update(currentLocation._id, {
            body: {
                openingHours: {
                    regular: currentLocation.openingHours?.regular ?? [],
                    overrides: overridesState
                }
            },
            headers: {
                'x-api-version': 13
            }
        });
        setIsSaving(false);

        if (response.ok) {
            setCurrentLocation(response.body);
            setIsEditing(false);
            enqueueSnackbar(MESSAGE_LOCATIONS_UPDATE_SUCCESS(currentLocation.title), { variant: 'success' });
        } else {
            const message = response.body.message ?? MESSAGE_LOCATIONS_UPDATE_ERROR;
            enqueueSnackbar(message, { variant: 'error' });
        }
    }, [currentLocation, overridesState, setCurrentLocation, enqueueSnackbar]);

    const deleteDateOverride = React.useCallback((id: string) => {
        setOverridesState(existing => existing.filter(o => o._id !== id));
    }, []);

    const editDateOverride = React.useCallback((formOverride: LocationOverrideOpeningHours) => {
        setDateOverride(formOverride);
        setShowAddDateOverride(true);
    }, []);

    const handleAddDateOverride = React.useCallback(() => {
        setShowAddDateOverride(true);
    }, []);

    const toggleEditing = React.useCallback(() => {
        setIsEditing(prev => !prev);
    }, []);

    const handleCancel = React.useCallback(() => {
        setIsEditing(false);
        setOverridesState(currentLocation.openingHours?.overrides ?? []);
    }, [currentLocation]);

    const onCloseDateOverrideModal = React.useCallback(() => {
        setShowAddDateOverride(false);
        setDateOverride(null);
    }, []);

    return (
        <Panel
            variant="outlined"
            title="Date Overrides"
            onClick={toggleEditing}
            showAction={!isEditing}
            divider
        >
            {overridesState.length === 0 ? (
                <Typography>No date overrides found</Typography>
            ) : (
                <Box display="flex" flexDirection="column" gap={2}>
                    {overridesState.map((apiOverride, index) => (
                        <OverrideItem
                            key={apiOverride._id ?? `date-override-${index}`}
                            apiOverride={apiOverride}
                            isEditing={isEditing}
                            isSaving={isSaving}
                            onEdit={editDateOverride}
                            onDelete={deleteDateOverride}
                        />
                    ))}
                </Box>
            )}
            {isEditing && (
                <Box display="flex" justifyContent="space-between" pt={2}>
                    <Box>
                        <IconButton onClick={handleAddDateOverride} disabled={isSaving}>
                            <AddIcon />
                        </IconButton>
                    </Box>

                    <Box display="flex" flexDirection="row" justifyContent="flex-end" gap={1}>
                        <Button size="small" variant="outlined" onClick={handleCancel} disabled={isSaving}>
                            Cancel
                        </Button>
                        <Button loading={isSaving} color="primary" variant="contained" onClick={handleSave}>
                            Submit
                        </Button>
                    </Box>
                </Box>
            )}
            <LocationDateOverrideModal
                open={showAddDateOverride}
                onClose={onCloseDateOverrideModal}
                dateOverride={dateOverride}
                onSave={onSubmit}
            />
        </Panel>
    );
};
